export default class FontSizeManager {
	/**
	 * Constructor for the FontSizeManager class
	 * @param {Element} container - An element that contains the increase, decrease and reset buttons
	 * @param {String} increase - A CSS selector for the increase button
	 * @param {String} decrease - A CSS selector for the decrease button
	 * @param {String} reset - A CSS selector for the reset button
	 * @param {Function} onIncrease - A callback function to be called when the increase button is clicked
	 * @param {Function} onReset - A callback function to be called when the reset button is clicked
	 * @param {Function} onDecrease - A callback function to be called when the decrease button is clicked
	 */
	constructor(
		container  = document.querySelector('[data-font-size="wrapper"]'),
		increase   = '[data-font-size="increase"]',
		decrease   = '[data-font-size="decrease"]',
		reset      = '[data-font-size="reset"]',
		onIncrease = () => {
		},
		onReset    = () => {
		},
		onDecrease = () => {
		},
	) {
		/** @type {HTMLElement} */
		this.container = container;

		/** @type {HTMLElement} */
		this.increase = container.querySelector(increase);
		
		/** @type {HTMLElement} */
		this.decrease = container.querySelector(decrease);
		
		/** @type {HTMLElement} */
		this.reset = container.querySelector(reset);
		
		/** @type {Number} */
		this.defaultSize = 16;
		
		/** @type {Number} */
		this.minSize = 10;
		
		/** @type {Number} */
		this.maxSize = 24;
		
		/** @type {Number} */
		this.currentSize = this.loadSavedSize();
		
		/** @type {Function} */
		this.onIncrease = onIncrease;
		
		/** @type {Function} */
		this.onDecrease = onDecrease;
		
		/** @type {Function} */
		this.onReset = onReset;
		
		this.applyFontSize();
	}
	
	/**
	 * @return {Number} The saved font size or the default size if there is no saved size.
	 */
	loadSavedSize() {
		return parseInt(localStorage.getItem('fontSize'), 10) || this.defaultSize;
	}
	
	/**
	 * @param {Number} size - The new font size to be saved.
	 *
	 * Saves the font size to local storage.
	 */
	// eslint-disable-next-line class-methods-use-this
	saveFontSize(size) {
		localStorage.setItem('fontSize', `${size}`);
	}
	
	/**
	 * Applies the current font size to the document element.
	 *
	 * @return {void}
	 */
	applyFontSize() {
		document.documentElement.style.fontSize = `${this.currentSize}px`;
	}
	
	/**
	 * Increases the font size of the document by one and saves the new font size
	 * to local storage.
	 *
	 * @return {void}
	 */
	increaseFontSize() {
		if (this.currentSize < this.maxSize) {
			this.currentSize += 1;
			this.applyFontSize();
			this.saveFontSize(this.currentSize);
			if (this.onIncrease) {
				this.onIncrease();
			}
		}
	}
	
	/**
	 * Decreases the font size of the document by one and saves the new font size
	 * to local storage.
	 *
	 * If the current font size is already at the minimum size, this method does
	 * nothing.
	 *
	 * @return {void}
	 */
	decreaseFontSize() {
		if (this.currentSize > this.minSize) {
			this.currentSize -= 1;
			this.applyFontSize();
			this.saveFontSize(this.currentSize);
			if (this.onDecrease) {
				this.onDecrease();
			}
		}
	}
	
	/**
	 * Resets the font size to the default size, applies the new font size to the
	 * document element and saves the new font size to local storage.
	 *
	 * @return {void}
	 */
	resetFontSize() {
		this.currentSize = this.defaultSize;
		this.applyFontSize();
		this.saveFontSize(this.currentSize);
		if (this.onReset) {
			this.onReset();
		}
	}
	
	/**
	 * Initializes the FontSizeManager by adding event listeners to the increase,
	 * decrease, and reset buttons.
	 *
	 * When the increase button is clicked, the `increaseFontSize` method is
	 * called. When the decrease button is clicked, the `decreaseFontSize` method
	 * is called. When the reset button is clicked, the `resetFontSize` method is
	 * called.
	 *
	 * @return {void}
	 */
	init() {
		this.increase.addEventListener('click', () => this.increaseFontSize());
		this.decrease.addEventListener('click', () => this.decreaseFontSize());
		this.reset.addEventListener('click', () => this.resetFontSize());
	}
}
